import React from 'react';
import { cn } from '../../lib/utils';

/**
 * OrkaBadge — generic semantic badge (PRD §7.2).
 *
 * Use for free-form labels. For *task state* prefer OrkaStatePill,
 * for *priority* prefer OrkaPriorityChip, for *agent tone* prefer
 * AgentStatusChip — they encode meaning through the type system.
 *
 *   <OrkaBadge tone="success">Synced</OrkaBadge>
 *   <OrkaBadge tone="warning" outline>Stale</OrkaBadge>
 */
const TONE_FILL = {
  neutral: 'bg-stone-100 text-stone-700 dark:bg-slate-800 dark:text-slate-300',
  info: 'bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300',
  success: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  danger: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300',
  intent: 'bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
};

const TONE_OUTLINE = {
  neutral: 'border-stone-300 text-stone-700 dark:border-slate-600 dark:text-slate-300',
  info: 'border-sky-300 text-sky-700 dark:border-sky-700 dark:text-sky-300',
  success: 'border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300',
  warning: 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-300',
  danger: 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300',
  intent: 'border-violet-300 text-violet-700 dark:border-violet-700 dark:text-violet-300',
};

const SIZE_CLASSES = {
  sm: 'text-[10px] h-5 px-1.5 gap-1',
  md: 'text-xs h-6 px-2 gap-1.5',
};

export function OrkaBadge({
  tone = 'neutral',
  outline = false,
  size = 'md',
  icon: Icon,
  children,
  className,
  ...rest
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full select-none',
        outline ? `border ${TONE_OUTLINE[tone]}` : TONE_FILL[tone],
        SIZE_CLASSES[size] || SIZE_CLASSES.md,
        className,
      )}
      {...rest}
    >
      {Icon && <Icon width={size === 'sm' ? 10 : 12} height={size === 'sm' ? 10 : 12} aria-hidden="true" />}
      {children}
    </span>
  );
}

export default OrkaBadge;
